import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Button, ImageWithLoader } from '../components/ui';
import { CashbackBadge } from '../components/CashbackBadge';
import { api } from '../services/api';
import { optimizeImage } from '../utils/image';
import { useCartStore, useFavoritesStore, useSettingsStore, useUserStore } from '../store';
import { Product, ProductVariant, Region, Settings } from '../types';
import {
  formatPrice,
  formatDate,
  getRegionName,
  getLanguageName,
  getEditionName,
  accountRate,
  formatPlatform,
} from '../utils/helpers';
import WebApp from '@twa-dev/sdk';

/* Region flag SVG components */
const FlagTR: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="18" height="17" viewBox="0 0 18 17" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect width="18" height="17" rx="4" fill="#E30917" />
    <g transform="translate(0 -0.5) scale(0.5)">
      <path fill="#EEE" d="M16 24a6 6 0 1 1 0-12c1.31 0 2.52.425 3.507 1.138A7.332 7.332 0 0 0 14 10.647A7.353 7.353 0 0 0 6.647 18A7.353 7.353 0 0 0 14 25.354c2.195 0 4.16-.967 5.507-2.492A5.963 5.963 0 0 1 16 24zm3.913-5.77l2.44.562l.22 2.493l1.288-2.146l2.44.561l-1.644-1.888l1.287-2.147l-2.303.98l-1.644-1.889l.22 2.494z" />
    </g>
  </svg>
);

const FlagIN: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="18" height="17" viewBox="0 0 18 17" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="flagIN-clip">
        <rect width="18" height="17" rx="4" />
      </clipPath>
    </defs>
    <g clipPath="url(#flagIN-clip)">
      <rect width="18" height="5.67" fill="#F93" />
      <rect y="5.67" width="18" height="5.67" fill="#EEE" />
      <rect y="11.33" width="18" height="5.67" fill="#138808" />
      <g transform="translate(0 -0.5) scale(0.5)">
        <circle fill="navy" cx="18" cy="18" r="4" />
        <circle fill="#EEE" cx="18" cy="18" r="3" />
        <path fill="#6666B3" d="M18 15l.146 2.264l1.001-2.035l-.73 2.147l1.704-1.498l-1.497 1.705l2.147-.731l-2.035 1.002L21 18l-2.264.146l2.035 1.001l-2.147-.73l1.497 1.704l-1.704-1.497l.73 2.147l-1.001-2.035L18 21l-.146-2.264l-1.002 2.035l.731-2.147l-1.705 1.497l1.498-1.704l-2.147.73l2.035-1.001L15 18l2.264-.146l-2.035-1.002l2.147.731l-1.498-1.705l1.705 1.498l-.731-2.147l1.002 2.035z" />
        <circle fill="navy" cx="18" cy="18" r="1" />
      </g>
    </g>
  </svg>
);

// СНГ (регион UA) — тот же флаг, что в корзине и переключателе регионов; до 15.10.2026
// здесь оставался польский от прежнего региона PL.
const FlagUA: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="18" height="17" viewBox="0 0 18 17" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="flagUA-clip">
        <rect width="18" height="17" rx="4" />
      </clipPath>
    </defs>
    <g clipPath="url(#flagUA-clip)">
      <rect width="18" height="8.5" fill="#005BBB" />
      <rect y="8.5" width="18" height="8.5" fill="#FFD500" />
    </g>
  </svg>
);

const REGION_FLAGS: Record<Region, React.ReactNode> = {
  TR: <FlagTR />,
  IN: <FlagIN />,
  UA: <FlagUA />,
};

/* Helper: offers per region and platform */
interface RegionOffer {
  region: Region;
  platform: string | null;
  variant: ProductVariant;
  price: number;
  oldPrice?: number;
  discount?: number;
}

const OFFER_REGIONS: Region[] = ['TR', 'IN', 'UA'];

// Все живые предложения карточки, по возрастанию ВЫЧИСЛЕННОЙ цены. В одном регионе
// их бывает несколько: у Sony «для PS4» и «для PS4/PS5» — разные покупки одного
// издания, покупатель выбирает платформу в карточке.
const getPlatformOffers = (product: Product, cfg?: Settings): RegionOffer[] => {
  // Цена варианта в рублях: диапазонный курс по регионалке (override товара приоритетнее).
  // Должна совпадать с расчетом в списке «Другие издания».
  const computePrice = (v: Product['variants'][number]) => {
    const rate = accountRate(v.region, v.regionalPrice || 0, product.rateOverrides, cfg);
    return (rate && v.regionalPrice) ? Math.ceil((v.regionalPrice * rate) / 5) * 5 : v.price;
  };

  return product.variants
    .filter((v) => v.available && OFFER_REGIONS.includes(v.region))
    .map((v) => ({
      region: v.region,
      platform: v.platform || null,
      variant: v,
      price: computePrice(v),
      oldPrice: v.oldPrice,
      discount: v.discount,
    }))
    .sort((a, b) => a.price - b.price);
};

// Самое дешевое предложение каждого региона — для выбора региона и цены «от».
const getRegionOffers = (product: Product, cfg?: Settings): RegionOffer[] => {
  const all = getPlatformOffers(product, cfg);
  const offers: RegionOffer[] = [];
  for (const region of OFFER_REGIONS) {
    const best = all.find((o) => o.region === region);
    if (best) offers.push(best);
  }
  return offers;
};

const getStaticInfo = (product: Product, selectedRegion?: string) => {
  const allVariants = product.category === 'donat' && product.parentGame
    ? product.parentGame.variants.filter((v) => v.available)
    : product.variants.filter((v) => v.available);

  const regionVariants = selectedRegion
    ? allVariants.filter((v) => v.region === selectedRegion)
    : [];
  const sourceVariants = regionVariants.length > 0 ? regionVariants : allVariants;

  const platforms = Array.from(
    new Set(sourceVariants.map((v) => v.platform).filter(Boolean))
  ).sort();

  const languages = Array.from(
    new Set(sourceVariants.map((v) => v.language).filter(Boolean))
  );

  const editions = Array.from(
    new Set(sourceVariants.map((v) => v.edition).filter(Boolean))
  );

  const SKIP_EDITIONS = new Set(['standard', 'standard edition', 'other', 'base', 'base edition', 'full game', 'full', 'default']);
  const primaryEdition = editions.find(e => e && !SKIP_EDITIONS.has(e.toLowerCase())) || editions[0] || null;

  const releaseDate = product.category === 'donat' && product.parentGame
    ? product.parentGame.releaseDate || product.releaseDate
    : product.releaseDate;

  return {
    platforms: platforms.length > 0 ? platforms.join(', ') : null,
    language: languages.length > 0 ? languages.map((l) => getLanguageName(l!)).join(', ') : null,
    edition: primaryEdition ? getEditionName(primaryEdition) : (product.category === 'game' ? 'Standard Edition' : null),
    releaseDate: releaseDate || null,
  };
};

/* ─── Other Editions Block with horizontal scroll + dots ─── */
const OtherEditionsBlock: React.FC<{
  editions: { id: string; edition: string; minPrice: number; coverImage: string }[];
  productName: string;
  fallbackCover: string;
  onSelect: (id: string) => void;
  title?: string;
  hideProductName?: boolean;
}> = ({ editions, productName, fallbackCover, onSelect, title, hideProductName }) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const visibleCount = 2;
  const totalPages = Math.ceil(editions.length / visibleCount);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) { setActiveIndex(0); return; }
    const ratio = el.scrollLeft / maxScroll;
    const idx = Math.round(ratio * (totalPages - 1));
    setActiveIndex(Math.min(idx, totalPages - 1));
  };

  return (
    <div>
      <h3 className="mb-3" style={{ fontFamily: 'Manrope, sans-serif', fontSize: '22px', fontWeight: 700, lineHeight: '120%', letterSpacing: '0px', color: '#1A1D1A' }}>{title || 'Другие издания:'}</h3>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide"
      >
        {editions.map(({ id: edId, edition, minPrice, coverImage }) => (
          <button
            key={edId}
            type="button"
            className="flex-shrink-0 w-[calc(50%-6px)] text-left active:scale-[0.97] transition-transform"
            onClick={() => onSelect(edId)}
          >
            <div className="mb-2.5">
              <ImageWithLoader
                src={optimizeImage(coverImage || fallbackCover, 300)}
                alt={getEditionName(edition)}
                wrapperClassName="w-full aspect-square rounded-[14px]"
                className="object-cover"
                spinnerSize={24}
              />
            </div>
            {!hideProductName && (
            <p className="truncate mb-0.5" style={{ fontFamily: 'Manrope, sans-serif', fontSize: '17px', fontWeight: 700, lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
              {productName}
            </p>
            )}
            <p className={`truncate${hideProductName ? ' mb-0.5' : ''}`} style={{ fontFamily: 'Manrope, sans-serif', fontSize: hideProductName ? '15px' : '13px', fontWeight: hideProductName ? 600 : 400, lineHeight: hideProductName ? '20px' : '16px', letterSpacing: '-0.08px', color: hideProductName ? '#1A1D1A' : '#818384' }}>
              {getEditionName(edition)}
            </p>
            <p className="mt-0.5" style={{ fontFamily: 'Manrope, sans-serif', fontSize: '17px', fontWeight: 700, lineHeight: '22px', color: '#1A1D1A' }}>
              {formatPrice(minPrice)}
            </p>
          </button>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex justify-center gap-1.5 mt-1">
          {Array.from({ length: totalPages }, (_, i) => (
            <div
              key={i}
              className="rounded-full transition-colors"
              style={{
                width: 6,
                height: 6,
                backgroundColor: i === activeIndex ? '#3657F0' : '#D1D5DB',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const addItem = useCartStore((s) => s.addItem);
  const itemsByRegion = useCartStore((s) => s.itemsByRegion);
  const cartItems = useMemo(
    () => [...(itemsByRegion.TR || []), ...(itemsByRegion.IN || []), ...(itemsByRegion.UA || [])],
    [itemsByRegion]
  );
  const { isFavorite, toggleFavorite } = useFavoritesStore();
  const { settings } = useSettingsStore();
  const userRegion = useUserStore((s) => s.lastSelectedRegion);
  const setLastSelectedRegion = useUserStore((s) => s.setLastSelectedRegion);

  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  // Выбранная платформа в регионе (id предложения); пусто — самое дешевое
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    loadProduct();
  }, [id]);

  // Sync selectedRegion when user changes global region
  useEffect(() => {
    if (userRegion) setSelectedRegion(userRegion);
  }, [userRegion]);

  const loadProduct = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await api.getProduct(id);
      if (data) {
        setProduct(data);
        const offers = getRegionOffers(data, settings);
        const enabledOffers = offers.filter(o => settings.availableRegions[o.region]);
        // Если регион пользователя выбран — используем его (даже если товар недоступен, покажем сообщение)
        if (userRegion) {
          setSelectedRegion(userRegion);
        } else if (enabledOffers.length > 0) {
          const cheapest = enabledOffers.reduce((min, o) => o.price < min.price ? o : min, enabledOffers[0]);
          setSelectedRegion(cheapest.region);
        } else if (offers.length > 0) {
          const cheapest = offers.reduce((min, o) => o.price < min.price ? o : min, offers[0]);
          setSelectedRegion(cheapest.region);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const regionOffers = useMemo(() => {
    if (!product) return [];
    return getRegionOffers(product, settings);
  }, [product, settings.exchangeRates, settings.rangePricing]);

  const otherEditions = useMemo(() => {
    if (!product?.otherEditions || product.otherEditions.length === 0) return [];
    return product.otherEditions.map(p => {
      // Каждое издание — по своему эффективному курсу (override издания → диапазон → плоский).
      const computeVariantPrice = (v: Product['variants'][number]) => {
        const rate = accountRate(v.region, v.regionalPrice || 0, p.rateOverrides, settings);
        return (rate && v.regionalPrice) ? Math.ceil((v.regionalPrice * rate) / 5) * 5 : v.price;
      };
      const enabledVariants = p.variants.filter(v => v.available && settings.availableRegions[v.region as Region]);
      // Та же логика, что и на странице издания: сначала регион пользователя (ВСЕГДА, без
      // завязки на availableRegions — иначе цена мини-карточки разойдется со страницей),
      // иначе самый дешевый доступный.
      const regionVariants = userRegion ? p.variants.filter(v => v.available && v.region === userRegion) : [];
      const source = regionVariants.length > 0 ? regionVariants : enabledVariants;
      const prices = source.map(computeVariantPrice).filter(pr => pr > 0);
      const minPrice = prices.length > 0 ? Math.min(...prices) : p.minPrice;
      const edition = product.category === 'topup'
        ? p.name
        : product.category === 'subscription' && p.subtitle
          ? p.subtitle
          : (p.variants.find(v => v.edition)?.edition || 'standard');
      return { id: p.id, edition, minPrice, coverImage: p.coverImage };
    }).sort((a, b) => a.minPrice - b.minPrice);
  }, [product, settings.exchangeRates, settings.rangePricing, settings.availableRegions, userRegion]);

  const platformOffers = useMemo(() => {
    if (!product) return [];
    return getPlatformOffers(product, settings);
  }, [product, settings.exchangeRates, settings.rangePricing]);

  // Предложения выбранного региона по платформам, от дешевого к дорогому
  const regionPlatformOffers = useMemo(
    () => (selectedRegion ? platformOffers.filter((o) => o.region === selectedRegion) : []),
    [platformOffers, selectedRegion],
  );

  // У Sony «для PS4» и «для PS4/PS5» — разные покупки одного издания, и в поиске они
  // показаны отдельными плитками. Плитка открывает карточку с этой версией (?platform=):
  // цена, строка «Платформа» и «Добавить в корзину» — по ней. Без параметра — самое дешевое.
  // Версию из ссылки выбираем здесь же, при первом расчете: раньше ее подставлял эффект
  // после первого рендера, и карточка на мгновение показывала цену PS4, а потом PS5.
  const selectedOffer = useMemo(() => {
    if (!selectedRegion) return null;
    const wanted = searchParams.get('platform');
    return regionPlatformOffers.find((o) => o.variant.id === selectedVariantId)
      || (wanted ? regionPlatformOffers.find((o) => (o.platform || '') === wanted) : undefined)
      || regionPlatformOffers[0]
      || null;
  }, [regionPlatformOffers, selectedRegion, selectedVariantId, searchParams]);

  const staticInfo = useMemo(() => {
    if (!product) return null;
    return getStaticInfo(product, selectedRegion || undefined);
  }, [product, selectedRegion]);

  const handleRegionChange = (region: Region) => {
    if (!settings.availableRegions[region]) return;
    setSelectedRegion(region);
  };

  const isInCart = selectedOffer && cartItems.some((item) => item.variant.id === selectedOffer.variant.id);

  const handleAddToCart = async () => {
    if (!product || !selectedOffer) return;
    await addItem(product, selectedOffer.variant);
  };

  const handleGoToCart = () => {
    navigate('/cart');
  };

  const handleToggleFavorite = () => {
    if (!product) return;
    toggleFavorite(product.id);
  };

  if (isLoading) {
    return <Layout showNav={false}><div /></Layout>;
  }

  if (!product) {
    return (
      <Layout showNav={false}>
        <div className="px-4 py-6">
          <p className="text-text-secondary">Продукт не найден</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            На главную
          </Button>
        </div>
      </Layout>
    );
  }

  const isLiked = isFavorite(product.id);

  return (
    <Layout showNav={false}>
      <div className="pb-16">
        {/* Cover Image — aspect-square резервирует высоту, чтобы контент не прыгал при загрузке */}
        <ImageWithLoader
          src={optimizeImage(product.coverImage, 800)}
          alt={product.name}
          wrapperClassName={`w-full aspect-square ${product.category === 'topup' ? '-mt-4' : ''}`}
          className="object-cover object-top w-full"
          style={product.category === 'topup' ? undefined : { maxHeight: '55vh' }}
          spinnerSize={32}
          loading="eager"
          fallbackSrc="https://via.placeholder.com/400x300?text=No+Image"
        />

        {/* Белая карточка с закругленными верхними краями */}
        <div className={`bg-white rounded-t-2xl pt-5 pb-5 px-4 relative z-10 ${product.category === 'topup' ? '-mt-12' : '-mt-5'}`}>
        {/* Название игры + избранное рядом */}
        <div className="mb-1.5 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '22px', fontWeight: 700, lineHeight: '120%', letterSpacing: '0px', color: '#1A1D1A' }}>
              {product.name}
            </h1>
            {staticInfo?.edition && (
              <p className="mt-0.5" style={{ fontSize: '13px', fontWeight: 400, lineHeight: '16px', letterSpacing: '-0.08px', color: '#818384' }}>
                {staticInfo.edition}
              </p>
            )}
          </div>
          <button
            onClick={handleToggleFavorite}
            className="flex-shrink-0 p-2 -mt-0.5 hover:bg-background-secondary rounded-xl transition-colors"
          >
            <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 17L7.695 15.7771C3.06 11.4507 0 8.58801 0 5.09537C0 2.2327 2.178 0 4.95 0C6.516 0 8.019 0.750409 9 1.92698C9.981 0.750409 11.484 0 13.05 0C15.822 0 18 2.2327 18 5.09537C18 8.58801 14.94 11.4507 10.305 15.7771L9 17Z" fill={isLiked ? '#3657F0' : '#BBBEC0'} />
            </svg>
          </button>
        </div>

        {/* Цена + скидка */}
        {selectedOffer ? (
          <div className="flex items-center flex-wrap gap-2.5 mb-4">
            <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '38px', lineHeight: '120%', color: '#1A1D1A' }}>
              {formatPrice(selectedOffer.price)}
            </span>
            {selectedOffer.discount && (
              <span className="bg-brand-gradient text-white text-[13px] font-bold px-1.5 py-0.5 rounded-md">
                -{selectedOffer.discount}%
              </span>
            )}
            <CashbackBadge price={selectedOffer.price} />
          </div>
        ) : userRegion && regionOffers.length > 0 ? (
          <div className="mb-4">
            <p className="text-[15px] font-semibold text-text-primary mb-1">
              Товар недоступен в регионе {getRegionName(userRegion)}
            </p>
            <p className="text-[13px] text-text-secondary mb-3">
              Выберите один из доступных регионов:
            </p>
            <div className="flex overflow-x-auto scrollbar-hide -mx-4 px-4" style={{ gap: '3px' }}>
              {regionOffers.map((offer) => (
                <button
                  key={offer.region}
                  onClick={() => setLastSelectedRegion(offer.region)}
                  className="flex-shrink-0 rounded-[11px] border border-[#818384]/50 active:scale-[0.97] transition-transform"
                  style={{ width: '177.5px', height: '78px', padding: '8px', paddingLeft: '17px', backgroundColor: 'rgba(129, 131, 132, 0.10)' }}
                >
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="flex-shrink-0 leading-none">{REGION_FLAGS[offer.region]}</span>
                    <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: 700, lineHeight: '120%', color: '#1A1D1A' }}>
                      {getRegionName(offer.region)}
                    </span>
                  </div>
                  <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '28px', fontWeight: 600, lineHeight: '120%', color: '#1A1D1A', textAlign: 'left' }}>
                    {formatPrice(offer.price)}
                  </p>
                </button>
              ))}
            </div>
          </div>
        ) : regionOffers.length > 0 ? (
          <div className="flex items-center flex-wrap gap-2.5 mb-4">
            <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '38px', lineHeight: '120%', color: '#1A1D1A' }}>
              {formatPrice(Math.min(...regionOffers.map(o => o.price)))}
            </span>
            {regionOffers.some(o => o.discount) && (
              <span className="bg-brand-gradient text-white text-[13px] font-bold px-1.5 py-0.5 rounded-md">
                -{Math.max(...regionOffers.filter(o => o.discount).map(o => o.discount!))}%
              </span>
            )}
            <CashbackBadge price={Math.min(...regionOffers.map(o => o.price))} />
          </div>
        ) : null}

        {/* Состав издания */}
        {product.description && (
          <div className="mb-5">
            <div className="bg-background-secondary rounded-card p-4">
              <h3 className="mb-3" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
                Состав издания:
              </h3>
              <ul className="space-y-1.5">
                {product.description.split('\n').filter(Boolean).map((line, i) => (
                  <li key={i} className="flex items-start gap-2" style={{ fontFamily: 'Manrope, sans-serif', fontSize: '15px', lineHeight: '20px', fontWeight: 400, color: '#1A1D1A' }}>
                    <span className="mt-2 w-1 h-1 rounded-full bg-[#1A1D1A] flex-shrink-0" />
                    {line.replace(/^[-•·]\s*/, '')}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Static product info */}
        <div className="mb-5">
          {product.category !== 'subscription' && product.category !== 'topup' && (
          <div className="bg-background-secondary rounded-card p-4 space-y-3">
            <div className="flex justify-between gap-4">
              <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#818384' }}>Платформа</span>
              <span className="text-right" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#1A1D1A' }}>{selectedOffer?.platform ? formatPlatform(selectedOffer.platform) : (staticInfo?.platforms || '—')}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#818384' }}>Язык</span>
              <span className="text-right" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#1A1D1A' }}>{staticInfo?.language || '—'}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#818384' }}>Издание</span>
              <span className="text-right" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#1A1D1A' }}>{staticInfo?.edition || '—'}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#818384' }}>Дата релиза</span>
              <span className="text-right" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: '15px', lineHeight: '20px', color: '#1A1D1A' }}>{formatDate(staticInfo?.releaseDate || product.releaseDate)}</span>
            </div>
          </div>
          )}
        </div>

        {/* Другие издания — горизонтальный скролл с точками */}
        {otherEditions.length > 0 && product && (
          <OtherEditionsBlock
            editions={otherEditions}
            productName={product.name}
            fallbackCover={product.coverImage}
            onSelect={(edId) => navigate(`/product/${edId}`, { replace: true })}
            title={product.category === 'subscription' ? 'Другие подписки PS Plus:' : product.category === 'topup' ? 'Другие карты пополнения:' : 'Другие издания:'}
            hideProductName={product.category === 'topup'}
          />
        )}

        </div>

        {/* Fixed bottom CTA */}
        <div
          className="fixed left-0 right-0 z-30 bg-white"
          style={{
            bottom: '0',
            paddingBottom: Math.max(
              (WebApp as any).safeAreaInset?.bottom || 0,
              (WebApp as any).contentSafeAreaInset?.bottom || 0,
              8
            ),
          }}
        >
          <div style={{ padding: '8px 16px 0 16px' }}>
            <Button
              onClick={isInCart ? handleGoToCart : handleAddToCart}
              disabled={!selectedOffer}
              fullWidth
              size="md"
              style={{ height: '52px', fontSize: '16px', fontWeight: 600, borderRadius: '16px' }}
            >
              {isInCart ? 'Перейти в корзину' : 'Добавить в корзину'}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};
